import { Link, Navigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { CTABand } from "../components/CTABand";
import { FadeUp, Ribbon } from "../components/Reveal";
import { useContent, imgSrc } from "../context/ContentContext";

export default function ProjectDetail() {
  const { slug } = useParams();
  const { projects } = useContent();
  const project = projects.find((p) => p.slug === slug);

  if (!project) return <Navigate to="/portfolio" replace />;

  const index = projects.findIndex((p) => p.slug === slug);
  const next = projects[(index + 1) % projects.length];

  return (
    <main data-testid={`project-page-${project.slug}`}>
      <section>
        <div className="mx-auto max-w-[1400px] px-4 pb-8 pt-10 sm:px-6">
          <FadeUp>
            <Link
              to="/portfolio"
              data-testid="project-back-link"
              className="inline-flex items-center gap-1.5 font-display text-sm font-bold uppercase tracking-wide text-[#8E8D8A] transition-colors duration-200 hover:text-[#111111]"
            >
              <ChevronLeft className="h-4 w-4" />
              Terug naar portfolio
            </Link>
          </FadeUp>
          <FadeUp delay={0.05}>
            <h1
              data-testid="project-title"
              className="mt-4 max-w-3xl font-display text-3xl font-bold uppercase leading-tight tracking-wide text-[#111111] sm:text-4xl"
            >
              {project.title}
            </h1>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="mt-6 flex flex-wrap gap-x-10 gap-y-4 border-y border-[#E2E0D9] py-4">
              {[
                ["Categorie", project.category],
                ["Locatie", project.location],
                ["Uitvoering", "In eigen beheer"],
              ].map(([label, value]) => (
                <div key={label} data-testid={`project-meta-${label.toLowerCase()}`}>
                  <p className="font-display text-xs font-bold uppercase tracking-[0.15em] text-[#8E8D8A]">
                    {label}
                  </p>
                  <p className="mt-1 text-sm text-[#333333]">{value}</p>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
        <FadeUp delay={0.1} className="mx-auto max-w-[1400px] px-4 pb-12 sm:px-6">
          <div className={`grid gap-6 ${project.gallery ? "lg:grid-cols-3" : ""}`}>
            <div className={`relative ${project.gallery ? "lg:col-span-2" : ""}`}>
              <img
                src={imgSrc(project.image.src)}
                alt={project.image.alt}
                className="h-[320px] w-full object-cover sm:h-[520px]"
              />
              <Ribbon className="absolute -bottom-4 left-4">{project.category}</Ribbon>
            </div>
            {project.gallery?.map((img, i) => (
              <img
                key={img.src}
                src={imgSrc(img.src)}
                alt={img.alt}
                data-testid={`project-gallery-${i}`}
                className="h-[320px] w-full object-cover sm:h-[520px]"
                loading="lazy"
              />
            ))}
          </div>
        </FadeUp>
      </section>

      <section className="bg-white">
        <div className="mx-auto grid max-w-[1400px] gap-10 px-4 py-12 sm:px-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <FadeUp>
              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-[#111111]">
                Over dit project
              </h2>
            </FadeUp>
            <FadeUp delay={0.05}>
              <p className="mt-4 text-[15px] font-medium leading-relaxed text-[#333333]">
                {project.summary}
              </p>
            </FadeUp>
            {project.body.map((para, i) => (
              <FadeUp key={i} delay={0.08 + i * 0.05}>
                <p data-testid={`project-body-${i}`} className="mt-4 text-[15px] leading-relaxed text-[#555555]">
                  {para}
                </p>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.1}>
            <div className="self-start bg-[#EFEDE8] p-6">
              <h3 className="font-display text-lg font-bold uppercase tracking-wide text-[#111111]">
                Werkzaamheden
              </h3>
              <ul className="mt-3">
                {project.work.map((w, i) => (
                  <li
                    key={w}
                    data-testid={`project-work-${i}`}
                    className="flex items-center gap-3 border-b border-[#DDD9D0] py-3 text-sm text-[#333333] last:border-b-0"
                  >
                    <ChevronRight className="h-4 w-4 shrink-0 text-[#8E8D8A]" />
                    {w}
                  </li>
                ))}
              </ul>
            </div>
          </FadeUp>
        </div>

        <div className="mx-auto max-w-[1400px] px-4 pb-12 sm:px-6">
          <FadeUp>
            <Link
              to={`/portfolio/${next.slug}`}
              data-testid="project-next-link"
              className="group flex items-center justify-between border-y border-[#E2E0D9] py-6"
            >
              <div>
                <p className="font-display text-xs font-bold uppercase tracking-[0.15em] text-[#8E8D8A]">
                  Volgend project
                </p>
                <p className="mt-1 font-display text-xl font-bold uppercase tracking-wide text-[#111111] sm:text-2xl">
                  {next.title}
                </p>
              </div>
              <ChevronRight className="h-6 w-6 text-[#8E8D8A] transition-all duration-300 group-hover:translate-x-1 group-hover:text-[#111111]" />
            </Link>
          </FadeUp>
        </div>
      </section>

      <CTABand />
    </main>
  );
}
